import { useEffect, useRef } from "react";
import { isTossApp } from "@/lib/platform";

const BANNER_AD_GROUP_ID = "ait.v2.live.99b8bab6444649b6";

/** TossAds.initialize() 전역 1회 실행 보장 */
let initPromise: Promise<typeof import("@apps-in-toss/web-framework")> | null = null;

function ensureInitialized() {
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve, reject) => {
    import("@apps-in-toss/web-framework")
      .then((sdk) => {
        sdk.TossAds.initialize({
          callbacks: {
            onInitialized: () => resolve(sdk),
            onInitializationFailed: (error: Error) => reject(error),
          },
        });
      })
      .catch(reject);
  });

  return initPromise;
}

interface Props {
  adGroupId?: string;
  className?: string;
}

/**
 * 토스 AIT 배너 광고
 * - 토스 앱: TossAds.attachBanner 렌더링
 * - 일반 웹: 렌더링 안 함
 */
export default function AdBanner({ adGroupId = BANNER_AD_GROUP_ID, className = "" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const destroyRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isTossApp() || !containerRef.current) return;

    let cancelled = false;

    ensureInitialized()
      .then((sdk) => {
        if (cancelled || !containerRef.current) return;
        const result = sdk.TossAds.attachBanner(adGroupId, containerRef.current, {
          theme: "auto",
          tone: "grey",
          variant: "expanded",
        });
        destroyRef.current = result.destroy;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      destroyRef.current?.();
      destroyRef.current = null;
    };
  }, [adGroupId]);

  if (!isTossApp()) return null;

  return <div ref={containerRef} className={`w-full ${className}`} />;
}
